import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Lock, Eye, EyeOff, CheckCircle, ArrowLeft, Star } from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  usePageTitle('Redefinir Senha');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Token de recuperação não encontrado');
      setTimeout(() => navigate('/'), 2000);
    }
  }, [token, navigate]);

  const validatePassword = (password: string) => {
    if (password.length < 6) {
      return 'A senha deve ter no mínimo 6 caracteres';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword
      });

      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao redefinir senha. O link pode estar expirado.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: '', color: 'bg-tinta/20' };
    if (password.length < 6) return { strength: 1, label: 'Fraca', color: 'bg-bordo' };
    if (password.length < 10) return { strength: 2, label: 'Média', color: 'bg-mostarda' };
    return { strength: 3, label: 'Forte', color: 'bg-musgo' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen paper-dark flex flex-col relative overflow-hidden font-body">
      {/* Decorative sparkles */}
      <Star className="sparkle w-8 h-8 text-mostarda" style={{ top: '8%', left: '10%' }} fill="currentColor" />
      <Star className="sparkle w-5 h-5 text-creme" style={{ top: '20%', right: '12%', animationDelay: '0.6s' }} fill="currentColor" />
      <Star className="sparkle w-10 h-10 text-mostarda" style={{ bottom: '12%', right: '8%', animationDelay: '1.1s' }} fill="currentColor" />
      <Star className="sparkle w-6 h-6 text-creme" style={{ bottom: '18%', left: '14%', animationDelay: '1.6s' }} fill="currentColor" />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-md w-full">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-creme/85 hover:text-mostarda transition-colors text-sm font-semibold mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o login
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-creme border-2 border-tinta rounded-full flex items-center justify-center"
              style={{ boxShadow: '4px 4px 0 0 #1A1A1A' }}>
              <Lock className="h-7 w-7 text-bordo" />
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black text-creme leading-tight">
              {success ? (
                <>Senha <span className="italic text-mostarda">redefinida</span>!</>
              ) : (
                <>Criar nova <span className="italic text-mostarda">senha</span></>
              )}
            </h1>
            <p className="mt-3 text-creme/85 italic">
              {success
                ? 'Você será redirecionado para o login'
                : 'Digite sua nova senha abaixo'}
            </p>
          </div>

          {success ? (
            <div className="space-y-6">
              <div className="card-vintage rounded-2xl p-7">
                <div className="text-center space-y-4">
                  <div className="mx-auto h-16 w-16 bg-musgo border-2 border-tinta rounded-full flex items-center justify-center"
                    style={{ boxShadow: '3px 3px 0 0 #1A1A1A' }}>
                    <CheckCircle className="h-8 w-8 text-creme" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-tinta">
                    Senha alterada com sucesso!
                  </h3>
                  <p className="text-tinta-soft">
                    Sua senha foi redefinida. Você já pode fazer login com sua nova senha.
                  </p>
                  <div className="pt-4">
                    <Link
                      to="/"
                      className="btn-vintage inline-flex items-center px-6 py-3 rounded-lg"
                    >
                      Ir para login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="card-vintage rounded-2xl p-7 space-y-5">
                {/* Nova Senha */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-bold text-tinta mb-1.5 uppercase tracking-wider">
                    Nova senha
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      className="input-vintage block w-full px-4 py-3 pr-12 rounded-lg text-base"
                      placeholder="Digite sua nova senha"
                      value={formData.newPassword}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-tinta-soft hover:text-bordo transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {formData.newPassword && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-tinta mb-1.5">
                        <span className="font-semibold uppercase tracking-wider">Força da senha</span>
                        <span className="font-bold italic">{passwordStrength.label}</span>
                      </div>
                      <div className="h-2 w-full bg-tinta/15 rounded-full overflow-hidden border border-tinta">
                        <div
                          className={`h-full ${passwordStrength.color} transition-all duration-300`}
                          style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
                        />
                      </div>
                      {formData.newPassword.length < 6 && (
                        <p className="mt-1.5 text-xs text-bordo italic">
                          Mínimo de 6 caracteres
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Confirmar Senha */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-bold text-tinta mb-1.5 uppercase tracking-wider">
                    Confirmar nova senha
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      className="input-vintage block w-full px-4 py-3 pr-12 rounded-lg text-base"
                      placeholder="Confirme sua nova senha"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-tinta-soft hover:text-bordo transition-colors"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Esconder senha' : 'Mostrar senha'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>

                  {/* Password Match Indicator */}
                  {formData.confirmPassword && (
                    <div className="mt-2">
                      {formData.newPassword === formData.confirmPassword ? (
                        <p className="text-xs text-musgo font-bold flex items-center italic">
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          As senhas coincidem
                        </p>
                      ) : (
                        <p className="text-xs text-bordo italic font-semibold">
                          As senhas não coincidem
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isLoading || formData.newPassword !== formData.confirmPassword || formData.newPassword.length < 6}
                  className="btn-vintage-mustard w-full flex items-center justify-center py-3.5 px-4 rounded-lg text-base"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-tinta"></div>
                  ) : (
                    <>
                      <Lock className="h-5 w-5 mr-2" />
                      Redefinir senha
                    </>
                  )}
                </button>

                <Link
                  to="/"
                  className="btn-vintage-light w-full flex items-center justify-center py-3.5 px-4 rounded-lg text-base"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Voltar para login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
